// ImageUpload.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ImageUploadProps {
  onImageSelect: (file: File) => void;
  initialImage?: string; // optional prop for edit modal
}

const ImageUpload: React.FC<ImageUploadProps> = ({ onImageSelect, initialImage }) => {
  const {t} = useTranslation()
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (initialImage) {
      setPreview(initialImage);
    }
  }, [initialImage]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      onImageSelect(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageSelect(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => document.getElementById('fileUpload')?.click()}
      className={`border-2 border-dashed rounded-md p-4 text-center transition-all cursor-pointer ${
        dragActive ? 'border-brand-400 bg-brand-50' : 'border-gray-300'
      }`}
    >
      <input
        type="file"
        id="fileUpload"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <p className="text-gray-600">{t('forms.image')}</p>
      {preview && (
        <img
          src={preview}
          alt="Preview"
          className="mt-4 max-h-36 mx-auto rounded-md shadow-md object-contain"
        />
      )}
    </div>
  );
};

export default ImageUpload;
